/**
 * PDF Utilities
 *
 * Handles PDF file detection, first-page thumbnail generation for uploads,
 * and on-demand page rendering for the PDF canvas node viewer.
 *
 * pdfjs-dist is loaded lazily on first use (~450KB) so it doesn't
 * bloat the initial bundle for users who never drop PDFs.
 */

import type { PDFDocumentProxy } from "pdfjs-dist";

/** Scale factor for rendering (2x for high-DPI) */
const RENDER_SCALE = 2;

/** Lazy-loaded pdfjs module */
let pdfjsPromise: Promise<typeof import("pdfjs-dist")> | null = null;

async function loadPdfjs(): Promise<typeof import("pdfjs-dist")> {
  if (!pdfjsPromise) {
    pdfjsPromise = import("pdfjs-dist").then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        "pdfjs-dist/build/pdf.worker.min.mjs",
        import.meta.url,
      ).href;
      return pdfjs;
    });
  }
  return pdfjsPromise;
}

// =============================================================================
// File Utilities
// =============================================================================

/**
 * Check if a file is a PDF.
 */
export function isPdf(file: File): boolean {
  return (
    file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
  );
}

/**
 * Get the page count of a PDF file.
 */
export async function getPdfPageCountFromFile(
  file: File | Blob,
): Promise<number> {
  try {
    const pdfjs = await loadPdfjs();
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
    const count = pdf.numPages;
    pdf.destroy();
    return count;
  } catch (err) {
    console.error("[pdf] Failed to get page count:", err);
    return 1;
  }
}

/**
 * Render the first page of a PDF file to a PNG blob.
 * Used during upload to create a thumbnail before the full page cache is ready.
 */
export async function pdfFirstPageToBlob(
  file: File | Blob,
): Promise<Blob | null> {
  try {
    const pdfjs = await loadPdfjs();
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);

    const viewport = page.getViewport({ scale: RENDER_SCALE });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({ canvas, viewport }).promise;
    pdf.destroy();

    return new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/png");
    });
  } catch (err) {
    console.error("[pdf] Failed to render first page:", err);
    return null;
  }
}

// =============================================================================
// PDF Page Cache — on-demand page rendering for the PDF viewer node
// =============================================================================

interface CachedPdf {
  doc: PDFDocumentProxy;
  totalPages: number;
  /** page number → blob URL of rendered page */
  pages: Map<number, string>;
  /** page number → in-flight render promise */
  pending: Map<number, Promise<string | null>>;
}

/** pdfUrl → cached PDF document and rendered pages */
const pdfCache = new Map<string, CachedPdf>();

/** pdfUrl → in-flight load promise */
const loadingDocs = new Map<string, Promise<CachedPdf | null>>();

/**
 * Load a PDF document from a blob URL or data URL (already fetched from SDK).
 * Returns the cached entry, or null on failure.
 */
async function loadPdfDoc(pdfBlobUrl: string): Promise<CachedPdf | null> {
  if (pdfCache.has(pdfBlobUrl)) return pdfCache.get(pdfBlobUrl)!;

  // Deduplicate concurrent loads
  if (loadingDocs.has(pdfBlobUrl)) return loadingDocs.get(pdfBlobUrl)!;

  const loadPromise = (async (): Promise<CachedPdf | null> => {
    try {
      const pdfjs = await loadPdfjs();

      // Fetch the blob URL to get an ArrayBuffer
      const response = await fetch(pdfBlobUrl);
      const arrayBuffer = await response.arrayBuffer();

      const doc = await pdfjs.getDocument({ data: arrayBuffer }).promise;
      const entry: CachedPdf = {
        doc,
        totalPages: doc.numPages,
        pages: new Map(),
        pending: new Map(),
      };
      pdfCache.set(pdfBlobUrl, entry);
      return entry;
    } catch (err) {
      console.error("[pdf] Failed to load PDF document:", err);
      return null;
    } finally {
      loadingDocs.delete(pdfBlobUrl);
    }
  })();

  loadingDocs.set(pdfBlobUrl, loadPromise);
  return loadPromise;
}

/**
 * Render a specific page of a PDF to a blob URL.
 *
 * @param pdfBlobUrl - A blob:// or data: URL of the PDF (already fetched from SDK media)
 * @param pageNum - 1-based page number
 * @returns blob URL of the rendered page, or null on failure
 */
export async function renderPdfPage(
  pdfBlobUrl: string,
  pageNum: number,
): Promise<string | null> {
  const entry = await loadPdfDoc(pdfBlobUrl);
  if (!entry) return null;

  // Clamp page number
  const page = Math.max(1, Math.min(pageNum, entry.totalPages));

  // Return cached page
  if (entry.pages.has(page)) return entry.pages.get(page)!;

  // Deduplicate concurrent renders of the same page
  if (entry.pending.has(page)) return entry.pending.get(page)!;

  const renderPromise = (async (): Promise<string | null> => {
    try {
      const pdfPage = await entry.doc.getPage(page);
      const viewport = pdfPage.getViewport({ scale: RENDER_SCALE });
      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await pdfPage.render({ canvas, viewport }).promise;

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), "image/png");
      });
      if (!blob) return null;

      const blobUrl = URL.createObjectURL(blob);
      entry.pages.set(page, blobUrl);
      return blobUrl;
    } catch (err) {
      console.error(`[pdf] Failed to render page ${page}:`, err);
      return null;
    } finally {
      entry.pending.delete(page);
    }
  })();

  entry.pending.set(page, renderPromise);
  return renderPromise;
}

/**
 * Get the total page count of a PDF.
 * Returns 0 if the PDF hasn't been loaded yet.
 */
export function getPdfPageCount(pdfBlobUrl: string): number {
  return pdfCache.get(pdfBlobUrl)?.totalPages ?? 0;
}

/**
 * Get a cached page blob URL (synchronous — returns null if not yet rendered).
 */
export function getCachedPdfPage(
  pdfBlobUrl: string,
  pageNum: number,
): string | null {
  return pdfCache.get(pdfBlobUrl)?.pages.get(pageNum) ?? null;
}

/**
 * Clean up all cached pages and documents for a given PDF URL.
 */
export function evictPdfCache(pdfBlobUrl: string): void {
  const entry = pdfCache.get(pdfBlobUrl);
  if (!entry) return;
  for (const blobUrl of entry.pages.values()) {
    URL.revokeObjectURL(blobUrl);
  }
  entry.doc.destroy();
  pdfCache.delete(pdfBlobUrl);
}

/**
 * Clean up all cached PDFs (call on space change).
 */
export function clearPdfCache(): void {
  for (const [url] of pdfCache) {
    evictPdfCache(url);
  }
}
